/**
 * GraderRemovalModal — remove the Grader role WITH A REASON, and its Reinstate counterpart (ADR-133 Amendment 2 §5,
 * figure 4's "Richard" path; COMMUNITY build 3, Stage B).
 *
 * One modal, two homes: Dashboard Access's Grader row (`PersonAccessPanel`) and the Grading page's Quality tab.
 *
 * WHAT A REMOVAL DOES, in order:
 *   1. S1 — `POST /admin/people/{sub}/access/grader/remove`: the attachment goes and a removal record stands, so
 *      finishing training again cannot grant the role back (the grant refuses 409 `removed`).
 *   2. S9 — `POST /admin/grading/graders/{sub}/removed`: every grade the grader gave is quarantined and the results
 *      they touched are recomputed (an owner's Engagement grade may be rewritten).
 * If step 2 fails the role IS removed — the modal says so and stays open on a Retry for step 2 alone, rather than
 * offering to remove again (which would 409) or closing as if everything were done.
 *
 * Standing directives: errors through the shared toast, never inline and never raw (`toastFromError`); the circle
 * close (`.portal-modal-close` via ActionConfirmModal).
 */
import { useEffect, useState } from 'react';
import { ActionConfirmModal } from './ActionConfirmModal';
import { toastFromError, useToast } from '../../components/ToastProvider';
import {
  GRADER_REMOVAL_REASONS,
  recheckRemovedGrader,
  reinstateGraderRole,
  removalReasonLine,
  removalReasonValid,
  removeGraderRole,
} from '../../lib/person-access-api';
import { zoneByCode } from '../../lib/zones';

export interface GraderRemovalModalProps {
  open: boolean;
  /** The grader's Cognito sub. */
  sub: string;
  /** How the person is named in the title — a name on Dashboard Access, the grader number on Quality. */
  label: string;
  onClose(): void;
  /** Called once the removal is complete (both halves), so the caller re-reads. */
  onDone(): void;
  /** The zone the Grader attachment lives in (a zone code). Omitted = the home zone. */
  zone?: string;
}

const box: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 16, color: '#e2e8f0', margin: '0 0 10px', cursor: 'pointer' };
const area: React.CSSProperties = {
  width: '100%', minHeight: 84, boxSizing: 'border-box', padding: '10px 12px', fontSize: 16, lineHeight: 1.4,
  background: '#0f172a', color: '#f1f5f9', border: '1px solid #475569', borderRadius: 10, resize: 'vertical',
};

export function GraderRemovalModal({ open, sub, label, onClose, onDone, zone }: GraderRemovalModalProps) {
  const toast = useToast();
  const [reasons, setReasons] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  /** Set once step 1 succeeded and step 2 failed: the role is gone; only the grade re-check is outstanding. */
  const [recheckOwed, setRecheckOwed] = useState(false);

  useEffect(() => { if (open) { setReasons([]); setNote(''); setRecheckOwed(false); setBusy(false); } }, [open, sub]);

  const valid = removalReasonValid(reasons, note);
  const z = zoneByCode(zone);
  const toggle = (k: string) => setReasons((r) => (r.includes(k) ? r.filter((x) => x !== k) : [...r, k]));

  const recheck = async (): Promise<boolean> => {
    try {
      await recheckRemovedGrader(sub, removalReasonLine(reasons, note), z);
      return true;
    } catch (err) {
      setRecheckOwed(true);
      toast.push({
        variant: 'error',
        title: 'Role removed — grades not yet re-checked',
        detail: `The Grader role is removed. Their grades will be re-checked when you press Retry. ${toastFromError(err).detail ?? ''}`.trim(),
      });
      return false;
    }
  };

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (!recheckOwed) {
        try {
          await removeGraderRole(sub, { reasons, note: note.trim() }, z);
        } catch (err) {
          toast.push({ variant: 'error', ...toastFromError(err, 'Could not remove the Grader role') });
          return;
        }
      }
      if (!(await recheck())) return;
      toast.push({ variant: 'success', title: 'Grader removed', detail: `${label} can no longer grade, and their grades are being re-checked.` });
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <ActionConfirmModal
      open={open}
      title={recheckOwed ? `Re-check ${label}'s grades` : `Remove ${label} from grading`}
      intent="danger"
      primaryLabel={recheckOwed ? 'Retry the re-check' : 'Remove'}
      primaryDisabled={!recheckOwed && !valid}
      loading={busy}
      onConfirm={() => void confirm()}
      // Once the role is gone, closing is still allowed — the removal stands either way; the caller re-reads.
      onCancel={() => { if (recheckOwed) onDone(); else onClose(); }}
    >
      {recheckOwed ? (
        <p data-grader-removal="recheck-owed">
          The Grader role is removed and they cannot re-join by training. Their grades have not been re-checked yet —
          press Retry so the results they touched are recomputed.
        </p>
      ) : (
        <>
          <p>
            The Grader role goes now, and a record stops them re-joining by finishing training. Every grade they gave
            is held out and the results they touched are recomputed. Staff can reinstate them later.
          </p>
          <fieldset style={{ border: 0, padding: 0, margin: '0 0 12px' }}>
            <legend style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 10 }}>Why?</legend>
            {GRADER_REMOVAL_REASONS.map((r) => (
              <label key={r.key} style={box}>
                <input
                  type="checkbox"
                  data-removal-reason={r.key}
                  checked={reasons.includes(r.key)}
                  onChange={() => toggle(r.key)}
                  style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0 }}
                />
                <span>{r.label}</span>
              </label>
            ))}
          </fieldset>
          <label style={{ display: 'block', fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 6 }} htmlFor="grader-removal-note">
            Note {reasons.length ? '(optional)' : '(required when no reason is ticked)'}
          </label>
          <textarea
            id="grader-removal-note"
            data-removal-note
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            style={area}
          />
        </>
      )}
    </ActionConfirmModal>
  );
}

export interface GraderReinstateModalProps {
  open: boolean;
  sub: string;
  label: string;
  /** Why the role was removed — shown so the reinstater sees what they are overturning. */
  removedBecause?: string;
  onClose(): void;
  onDone(): void;
  zone?: string;
}

/** Reinstate a removed grader: a reason is required; the role returns with the pool it had. */
export function GraderReinstateModal({ open, sub, label, removedBecause, onClose, onDone, zone }: GraderReinstateModalProps) {
  const toast = useToast();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setNote(''); setBusy(false); } }, [open, sub]);

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await reinstateGraderRole(sub, note.trim(), zoneByCode(zone));
      toast.push({ variant: 'success', title: 'Grader reinstated', detail: `${label} can grade again.` });
      onDone();
    } catch (err) {
      toast.push({ variant: 'error', ...toastFromError(err, 'Could not reinstate the Grader role') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <ActionConfirmModal
      open={open}
      title={`Reinstate ${label} as a grader`}
      intent="primary"
      primaryLabel="Reinstate"
      primaryDisabled={note.trim().length < 3}
      loading={busy}
      onConfirm={() => void confirm()}
      onCancel={onClose}
    >
      {removedBecause && <p>Removed because: {removedBecause}</p>}
      <p>The Grader role returns with the pool it had. Grades held out by the removal stay held out.</p>
      <label style={{ display: 'block', fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 6 }} htmlFor="grader-reinstate-note">
        Reason (required)
      </label>
      <textarea
        id="grader-reinstate-note"
        data-reinstate-note
        value={note}
        maxLength={500}
        onChange={(e) => setNote(e.target.value)}
        style={area}
      />
    </ActionConfirmModal>
  );
}
